import { useState, useEffect, useRef, type ReactNode } from 'react';
import { api, ApiError } from '../lib/api';

/* ============================================================================
   THE CONSENT GATE

   Wraps every authenticated route. Until the user accepts the current privacy
   notice, they see this and nothing else.

   ⚠ THIS SCREEN IS NOT THE SECURITY CONTROL.

   A blocked screen is a suggestion — anyone can open devtools or call the API
   with curl. The REAL gate is the server-side middleware, which refuses every
   request with 451 until they've accepted.

   This screen exists so that a normal person meets the notice in a way they can
   actually read, rather than a wall of 451 errors.

   ⚠ AND THE TEXT IS NOT MINE.

   The notice body comes from the database — your advocate's words, pasted into
   the admin console. A consent screen with wording invented by a developer is
   WORSE than no consent screen, because you would be relying on it.
   ========================================================================== */

interface Notice { id: string; version: string; body: string; }
interface GateStatus { mustAccept: boolean; notice: Notice | null; }

export function ConsentGate({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<GateStatus | null>(null);

  useEffect(() => {
    api.get<GateStatus>('/api/privacy/gate')
      .then(setStatus)
      // If the check itself fails, DON'T lock the school out of their own product. The server-side
      // middleware is the real gate — it will refuse anything that matters. A frontend that
      // fails closed on a network blip is a frontend that takes a school offline.
      .catch(() => setStatus({ mustAccept: false, notice: null }));
  }, []);

  if (!status) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
        <span className="sk" style={{ width: 200 }} />
      </div>
    );
  }

  if (status.mustAccept && status.notice) {
    return <NoticeScreen notice={status.notice} onAccepted={() => setStatus({ mustAccept: false, notice: null })} />;
  }

  return <>{children}</>;
}

/* -------------------------------------------------------------------------- */

function NoticeScreen({ notice, onAccepted }: { notice: Notice; onAccepted: () => void }) {
  const [readToEnd, setReadToEnd] = useState(false);
  const [ticked, setTicked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  /**
   * ⚠ THEY MUST SCROLL TO THE BOTTOM BEFORE THEY CAN AGREE.
   *
   * Not a dark pattern — the opposite. "I accept" on a notice the person demonstrably never scrolled
   * through is a consent a regulator will pick apart in seconds. Forcing the scroll is what makes the
   * acceptance defensible.
   */
  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    // 40px of slack — a scrollbar that never quite reaches the end is a rage-inducing bug.
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) setReadToEnd(true);
  };

  // A short notice that doesn't overflow can never fire a scroll event — so it's already "read".
  useEffect(() => {
    const el = scrollRef.current;
    if (el && el.scrollHeight <= el.clientHeight + 40) setReadToEnd(true);
  }, [notice.body]);

  const accept = async () => {
    setBusy(true); setError(null);
    try {
      await api.post('/api/privacy/accept', { version: notice.version });
      onAccepted();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not record your acceptance. Try again.');
    } finally { setBusy(false); }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: 'var(--bg, #0A1220)',
      display: 'grid', placeItems: 'center', padding: 20,
      overflowY: 'auto',
    }}>
      <div className="card" style={{
        maxWidth: 720, width: '100%', padding: 0, overflow: 'hidden',
        display: 'flex', flexDirection: 'column', maxHeight: '92vh',
      }}>
        <div style={{ padding: '22px 26px 16px', borderBottom: '1px solid var(--line)' }}>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Before you continue</div>
          <h1 className="h-display" style={{ fontSize: 23 }}>Privacy notice &amp; terms</h1>
          <p style={{ color: 'var(--ink-2)', fontSize: 13.5, marginTop: 6 }}>
            Please read this. You'll need to accept it before using the app.
          </p>
        </div>

        {/* The notice. The school's advocate wrote this — not us. */}
        <div
          ref={scrollRef}
          onScroll={onScroll}
          style={{
            flex: 1, overflowY: 'auto', padding: '20px 26px',
            fontSize: 14, lineHeight: 1.65, color: 'var(--ink)',
            whiteSpace: 'pre-wrap',
          }}
        >
          {notice.body}
        </div>

        <div style={{ padding: '16px 26px 22px', borderTop: '1px solid var(--line)' }}>
          {!readToEnd && (
            <p style={{ fontSize: 12.5, color: 'var(--ink-3)', marginBottom: 10, textAlign: 'center' }}>
              Scroll to the end to continue.
            </p>
          )}

          {error && (
            <div className="pill pill-danger" style={{ padding: '9px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>
              {error}
            </div>
          )}

          <label style={{
            display: 'flex', gap: 10, alignItems: 'flex-start',
            fontSize: 14, marginBottom: 14,
            cursor: readToEnd ? 'pointer' : 'default',
            opacity: readToEnd ? 1 : 0.45,
          }}>
            <input
              type="checkbox"
              checked={ticked}
              disabled={!readToEnd}
              onChange={(e) => setTicked(e.target.checked)}
              style={{ marginTop: 3 }}
            />
            <span>
              I have read and I accept this notice.
            </span>
          </label>

          <button
            className="btn btn-accent"
            style={{ width: '100%', justifyContent: 'center', padding: 14 }}
            disabled={!readToEnd || !ticked || busy}
            onClick={accept}
          >
            {busy ? 'Recording…' : 'Agree and continue'}
          </button>

          <p style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 10, textAlign: 'center' }}>
            Version {notice.version} · your acceptance is recorded with the date and time.
          </p>
        </div>
      </div>
    </div>
  );
}
